document.addEventListener('DOMContentLoaded', () => {
  const config = window.cartMotivatorConfig || {};
  const tiers = config.tiers || window.cartMotivatorTiers || [];
  if (!tiers.length) return;

  const normalizedTiers = tiers.map((t, idx) => ({
    index: t.index ?? (idx + 1),
    goal: Number(t.goal),
    variant_ids: (t.variant_ids || t.vids || []).map(v => Number(v)),
  }));

  const isCartPage = /\/cart\/?$/.test(window.location.pathname);
  const GIFT_HEADER = 'X-Cart-Motivator';

  const normalizeUrl = (u = '') => {
    try {
      const x = new URL(u, window.location.href);
      return x.pathname + x.search;
    } catch (_) {
      return String(u || '');
    }
  };

  const isCartMutationUrl = (url = '') => {
    const u = normalizeUrl(url);
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(u);
  };

  const getShippingEligibleTotal = (cart) => {
    return (cart.items || []).reduce((sum, item) => {
      if (item.requires_shipping) sum += Number(item.final_line_price || 0);
      return sum;
    }, 0);
  };

  const fetchCart = () => fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());

  const cartPost = (endpoint, payload) =>
    fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        [GIFT_HEADER]: '1',
      },
      body: JSON.stringify(payload),
    });

  const buildGiftOps = (cart) => {
    const subtotal = Number(cart.items_subtotal_price || 0);
    const eligibleTotal = getShippingEligibleTotal(cart);
    const freeShippingThreshold = Number(config.free_shipping_threshold || 0);
    const canUnlockTiers = freeShippingThreshold <= 0 || eligibleTotal >= freeShippingThreshold;
    const ops = [];
    const items = cart.items || [];
    
    normalizedTiers.forEach(tier => {
      const goalMet = canUnlockTiers && tier.goal > 0 && subtotal >= tier.goal;

      tier.variant_ids.forEach(variantId => {
        const existing = items.find(i => i.variant_id === variantId);

        if (goalMet && !existing) {
          ops.push(() => cartPost('/cart/add.js', { id: variantId, quantity: 1 }));
        }
        if (!goalMet && existing) {
          ops.push(() => cartPost('/cart/change.js', { id: existing.key, quantity: 0 }));
        }
        if (goalMet && existing && existing.quantity > 1) {
          ops.push(() => cartPost('/cart/change.js', { id: existing.key, quantity: 1 }));
        }
      });
    });

    return ops;
  };

  // Helper to intercept which HTML sections the theme is trying to update
  const getRequestedSections = (url, init) => {
    let sections = [];
    try {
      const u = new URL(url, window.location.origin);
      if (u.searchParams.has('sections')) {
        sections.push(...u.searchParams.get('sections').split(','));
      }
      if (init && init.body) {
        if (typeof init.body === 'string') {
          const bodyObj = JSON.parse(init.body);
          if (bodyObj.sections) {
            if (Array.isArray(bodyObj.sections)) sections.push(...bodyObj.sections);
            else if (typeof bodyObj.sections === 'string') sections.push(...bodyObj.sections.split(','));
          }
        } else if (init.body instanceof FormData && init.body.has('sections')) {
          sections.push(...init.body.get('sections').split(','));
        } else if (init.body instanceof URLSearchParams && init.body.has('sections')) {
          sections.push(...init.body.get('sections').split(','));
        }
      }
    } catch (e) {}
    return [...new Set(sections)].filter(Boolean).join(',');
  };

  const originalFetch = window.fetch;

  // The Master Interceptor
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    const normalizedUrl = normalizeUrl(url);
    const isCartMutation = isCartMutationUrl(normalizedUrl);
    
    const isOurOwn = init?.headers && (
      (init.headers instanceof Headers && init.headers.get(GIFT_HEADER)) ||
      (typeof init.headers === 'object' && init.headers[GIFT_HEADER])
    );

    // 1. If it's a normal fetch or our background code, let it run normally
    if (!isCartMutation || isOurOwn) {
      return originalFetch(input, init);
    }

    // 2. Let the theme's add/change process run first
    const response = await originalFetch(input, init);
    
    // 3. See which UI elements the theme wants to update (e.g. 'cart-drawer', 'cart-icon-bubble')
    const requestedSections = getRequestedSections(normalizedUrl, init);

    // 4. Check the new cart state to see if gifts need adjustments
    const cart = await fetchCart();
    const ops = buildGiftOps(cart);

    // 5. If no gift changes are needed, just return the normal response to the theme
    if (!ops.length) {
      return response;
    }

    // 6. Execute the necessary gift additions/removals
    for (const op of ops) {
      await op();
    }

    // 7. If we are on the main /cart page, just reload
    if (isCartPage) {
      window.location.reload();
      return new Promise(() => {}); // Halt execution to prevent errors before reload
    }

    // 8. CRITICAL FIX FOR DRAWER: Re-fetch the requested sections so they include the new gifts, 
    // and pass THIS new response to the theme instead. 
    if (requestedSections) {
      const updatedSectionsResponse = await fetch(`/cart/update.js?sections=${requestedSections}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [GIFT_HEADER]: '1' },
        body: JSON.stringify({ updates: {} }) 
      });
      return updatedSectionsResponse; 
    }

    // Fallback if no sections were requested
    const finalCart = await fetchCart();
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: finalCart } }));
    return response;
  };

  // Run once on initial page load in case a user abandoned a cart out of sync
  (async function initSync() {
    const cart = await fetchCart();
    const ops = buildGiftOps(cart);
    if (ops.length) {
      for (const op of ops) await op();
      if (isCartPage) {
        window.location.reload();
      } else {
         document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    }
  })();
});